import { useEffect, useState } from 'react';
import { DICOMApprovalStatus, Roi } from '~/domain/dicom/dicomstructureset';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { Image3D } from '~/domain/patient/image3d';
import { StructureSet } from '~/domain/patient/structureset';

const useExportSelections = (
    exportableStructureSets: StructureSet[] = [],
    exportableDoses: DoseMatrix[] = [],
    modalOpen?: boolean
) => {
    const [selectedImages, setSelectedImages] = useState<Image3D[]>([]);
    const [selectedStructureSets, setSelectedStructureSets] = useState<
        StructureSet[]
    >([]);
    const [selectedDoses, setSelectedDoses] = useState<DoseMatrix[]>([]);
    const [structureSelections, setStructureSelections] = useState<
        Record<string, Roi[]>
    >({});

    useEffect(() => {
        const newStructureSets = exportableStructureSets.filter(
            (ss) => ss.isNew
        );
        const newDoses = exportableDoses.filter((dose) => dose.isNew);
        if (newStructureSets?.length > 0) {
            setSelectedStructureSets(newStructureSets);
            newStructureSets.forEach((structureSet) => {
                initializeStructureSelections(structureSet);
            });
        }
        if (newDoses?.length > 0) {
            setSelectedDoses(newDoses);
        }
    }, [modalOpen]);

    const initializeStructureSelections = (structureSet: StructureSet) => {
        const approvedStructures = structureSet.roiArray.filter(
            (structure) => structure.isApproved
        );
        setStructureSelections((prev) => ({
            ...prev,
            [structureSet.dicomUid]: approvedStructures.length
                ? approvedStructures
                : structureSet.roiArray.filter(
                      (structure) =>
                          structure.approvalStatus !==
                          DICOMApprovalStatus.REJECTED
                  ),
        }));
    };

    const handleSelectImage = (image: Image3D) => {
        setSelectedImages((prev) =>
            prev.includes(image)
                ? prev.filter((id) => id !== image)
                : [...prev, image]
        );
    };

    const handleSelectStructureSet = (structureSet: StructureSet) => {
        setSelectedStructureSets((prev) =>
            prev.includes(structureSet)
                ? prev.filter((id) => id !== structureSet)
                : [...prev, structureSet]
        );

        if (!structureSelections[structureSet.dicomUid]) {
            initializeStructureSelections(structureSet);
        }
    };

    const handleSelectStructure = (structureSetId: string, structure: Roi) => {
        setStructureSelections((prev) => {
            const currentSelections = prev[structureSetId] || [];
            const updatedSelections = currentSelections.includes(structure)
                ? currentSelections.filter((s) => s !== structure)
                : [...currentSelections, structure];

            return {
                ...prev,
                [structureSetId]: updatedSelections,
            };
        });
    };

    const handleSelectDose = (dose: DoseMatrix) => {
        setSelectedDoses((prev) =>
            prev.includes(dose)
                ? prev.filter((id) => id !== dose)
                : [...prev, dose]
        );
    };

    const resetSelections = () => {
        setSelectedImages([]);
        setSelectedStructureSets([]);
        setSelectedDoses([]);
        setStructureSelections({});
    };

    return {
        selectedImages,
        selectedStructureSets,
        selectedDoses,
        structureSelections,
        handleSelectImage,
        handleSelectStructureSet,
        handleSelectStructure,
        handleSelectDose,
        resetSelections,
    };
};

export default useExportSelections;
